import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useTray } from "../context/TrayContext";
import { DEFAULT_SPICE } from "../lib/spice";
import SpiceSlider from "./SpiceSlider";
import DatePicker from "./DatePicker";
import FulfillmentToggle from "./FulfillmentToggle";

export const TrayBuilder = () => {
  const { list, subtotal, deliveryFee, total, count, setQty, setSpice, remove, clear, fulfillment } = useTray();

  return (
    <div
      data-testid="tray-builder"
      className="overflow-hidden rounded-[1.5rem] border border-nosh-border bg-white shadow-[0_20px_50px_-25px_rgba(36,32,26,0.35)]"
    >
      <div className="flex items-center justify-between border-b border-nosh-border px-6 py-5">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-nosh-green/10 text-nosh-green">
            <ShoppingBag className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-2xl font-bold leading-none text-nosh-ink">Your Order</h3>
            <p className="font-poster text-[10px] font-semibold uppercase tracking-widest text-nosh-ink/45">
              Build your own feast
            </p>
          </div>
        </div>
        <span className="font-poster text-xs font-semibold uppercase tracking-wide text-nosh-ink/45">
          {count} {count === 1 ? "item" : "items"}
        </span>
      </div>

      <div className="max-h-[38vh] overflow-y-auto px-5 py-4">
        {list.length === 0 ? (
          <div className="py-10 text-center">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full border-2 border-dashed border-nosh-border">
              <ShoppingBag className="h-6 w-6 text-nosh-ink/30" />
            </div>
            <p className="mx-auto max-w-[15rem] font-body text-sm text-nosh-ink/55">
              Tap <span className="font-semibold text-nosh-green">Half</span> or{" "}
              <span className="font-semibold text-nosh-green">Full</span> on any dish to start building
              your feast.
            </p>
          </div>
        ) : (
          <ul className="space-y-2.5">
            <AnimatePresence initial={false}>
              {list.map((item) => (
                <motion.li
                  key={item.key}
                  layout
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.25 }}
                  className="rounded-xl bg-nosh-surface p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-display text-base font-bold text-nosh-ink">{item.name}</div>
                      <div className="font-poster text-[10px] font-semibold uppercase tracking-wide text-nosh-ink/45">
                        {item.size} · ${item.price}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setQty(item.key, item.qty - 1)} aria-label="Decrease" className="grid h-7 w-7 place-items-center rounded-full border border-nosh-border text-nosh-ink transition-colors hover:border-nosh-green hover:text-nosh-green">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-5 text-center font-poster text-sm font-bold text-nosh-ink">{item.qty}</span>
                      <button onClick={() => setQty(item.key, item.qty + 1)} aria-label="Increase" className="grid h-7 w-7 place-items-center rounded-full border border-nosh-border text-nosh-ink transition-colors hover:border-nosh-green hover:text-nosh-green">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <button onClick={() => remove(item.key)} aria-label="Remove" className="grid h-7 w-7 place-items-center rounded-full text-nosh-ink/35 transition-colors hover:text-nosh-clay">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <SpiceSlider
                    value={item.spice ?? DEFAULT_SPICE}
                    onChange={(lvl) => setSpice(item.key, lvl)}
                    testId={`spice-slider-${item.key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  />
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>

      {/* Fulfillment + date */}
      <div className="space-y-3 border-t border-nosh-border px-6 py-5">
        <div>
          <div className="mb-2 font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green">How would you like it?</div>
          <FulfillmentToggle />
        </div>
        <div>
          <div className="mb-2 font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green">Select your event date</div>
          <DatePicker />
        </div>
      </div>

      {/* Totals */}
      <div className="border-t border-nosh-border bg-nosh-surface px-6 py-5">
        <div className="space-y-1.5 font-body text-sm text-nosh-ink/75">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span data-testid="tray-subtotal" className="font-semibold text-nosh-ink">${subtotal}</span>
          </div>
          <div className="flex justify-between">
            <span>{fulfillment} {fulfillment === "Delivery" ? "fee" : ""}</span>
            <span className="font-semibold text-nosh-ink">{deliveryFee ? `$${deliveryFee}` : "Free"}</span>
          </div>
        </div>
        <div className="mt-3 flex items-end justify-between border-t border-nosh-border pt-3">
          <span className="font-poster text-xs font-bold uppercase tracking-widest text-nosh-ink/60">Estimated total</span>
          <span data-testid="tray-total" className="font-display text-3xl font-bold text-nosh-green">${total}</span>
        </div>
        <p className="mt-1 font-body text-[11px] text-nosh-ink/45">Final quote confirmed on booking. Serving sizes vary by guest count.</p>
        <p className="mt-1.5 flex items-center gap-1.5 font-body text-[11px] font-medium text-nosh-green">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-nosh-gold" />
          We accept Zelle &amp; Venmo — paid in advance once your order is confirmed.
        </p>

        <div className="mt-4 flex items-center gap-3">
          <Link
            to="/about#inquiry"
            data-testid="tray-request-cta"
            className={`group flex flex-1 items-center justify-center gap-2 rounded-full px-5 py-3.5 font-poster text-sm font-bold uppercase tracking-wide transition-transform duration-300 ${
              count > 0 ? "bg-nosh-green text-nosh-cream hover:-translate-y-0.5" : "cursor-not-allowed bg-nosh-border/60 text-nosh-ink/40"
            }`}
            onClick={(e) => count === 0 && e.preventDefault()}
          >
            Request Catering
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          {count > 0 && (
            <button data-testid="tray-clear" onClick={clear} className="font-poster text-[11px] font-bold uppercase tracking-wide text-nosh-ink/45 transition-colors hover:text-nosh-clay">
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrayBuilder;
